const KEY_PREFIX = {
  DRAFT: 'draft:',
  LINEAGE: 'lineage:',
} as const;

export interface DraftWriteScope {
  draftId?: string;
  lineageId?: string;
}

const tails = new Map<string, Promise<void>>();
const pendingCounts = new Map<string, number>();

const noop = (): void => undefined;

export const draftWriteKeys = ({
  draftId,
  lineageId,
}: DraftWriteScope): string[] => [
  ...(draftId ? [`${KEY_PREFIX.DRAFT}${draftId}`] : []),
  ...(lineageId ? [`${KEY_PREFIX.LINEAGE}${lineageId}`] : []),
];

const release = (key: string, tail: Promise<void>): void => {
  const remaining = (pendingCounts.get(key) ?? 1) - 1;
  if (remaining > 0) {
    pendingCounts.set(key, remaining);
    return;
  }
  pendingCounts.delete(key);
  if (tails.get(key) === tail) {
    tails.delete(key);
  }
};

const track = (keys: string[], tail: Promise<void>): void => {
  for (const key of keys) {
    tails.set(key, tail);
    pendingCounts.set(key, (pendingCounts.get(key) ?? 0) + 1);
  }
  void tail.then(() => keys.forEach((key) => release(key, tail)));
};

// Writes sharing any key run strictly one after another, in the order they were issued.
export const enqueueDraftWrite = <T>(
  keys: string[],
  task: () => Promise<T>,
): Promise<T> => {
  const previous = Promise.all(keys.map((key) => tails.get(key))).then(noop);
  const run = previous.then(task);
  track(keys, run.then(noop, noop));
  return run;
};

export const hasPendingDraftWrite = (scope: DraftWriteScope): boolean =>
  draftWriteKeys(scope).some((key) => (pendingCounts.get(key) ?? 0) > 0);

// A create learns its id only when it lands: later writes by id must queue behind it.
export const handOverWriteKey = (
  from: DraftWriteScope,
  to: DraftWriteScope,
): void => {
  const [fromKey] = draftWriteKeys(from);
  const [toKey] = draftWriteKeys(to);
  const tail = fromKey ? tails.get(fromKey) : undefined;
  if (tail && toKey && !tails.has(toKey)) {
    track([toKey], tail);
  }
};
